// =============================================================================
// Admin Module Constants
// =============================================================================

// -----------------------------------------------------------------------------
// Design System Colors (aligned with Tailwind config)
// -----------------------------------------------------------------------------
export const COLORS = {
  primary: '#F4CC04',
  primaryHover: '#D4B104',
  background: {
    dark: '#0a0a0a',
    darkSecondary: '#1a1a1a',
    card: '#2a2a2a',
  },
  text: {
    white: '#FFFFFF',
    gray: '#CCCCCC',
    darkGray: '#888888',
    tertiary: '#999999',
  },
  error: '#e53935',
  warning: '#FFA726',
  success: '#4CAF50',
  border: '#333333',
} as const;

// -----------------------------------------------------------------------------
// Admin Routes
// -----------------------------------------------------------------------------
export const ADMIN_ROUTES = {
  dashboard: '/admin',
  metrics: '/admin/metrics',
  agents: '/admin/agents',
  agentDetail: '/admin/agents/:id',
  databases: '/admin/databases',
  databaseDetail: '/admin/databases/:id',
  datasources: '/admin/datasources',
  datasourceDetail: '/admin/datasources/:id',
  settings: '/admin/settings',
} as const;

// -----------------------------------------------------------------------------
// Navigation Items
// -----------------------------------------------------------------------------
export const ADMIN_NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', path: ADMIN_ROUTES.dashboard, icon: 'LayoutDashboard' },
  { id: 'metrics', label: 'Métricas', path: ADMIN_ROUTES.metrics, icon: 'BarChart3' },
  { id: 'agents', label: 'Agentes', path: ADMIN_ROUTES.agents, icon: 'Bot' },
  { id: 'databases', label: 'Bases de Conhecimento', path: ADMIN_ROUTES.databases, icon: 'Database' },
  { id: 'datasources', label: 'Data Sources', path: ADMIN_ROUTES.datasources, icon: 'FileText' },
  { id: 'settings', label: 'Configurações', path: ADMIN_ROUTES.settings, icon: 'Cog' },
] as const;

// -----------------------------------------------------------------------------
// Pagination
// -----------------------------------------------------------------------------
export const PAGINATION = {
  defaultPageSize: 10,
  pageSizeOptions: [10, 25, 50, 100],
} as const;

// -----------------------------------------------------------------------------
// User Roles
// -----------------------------------------------------------------------------
export const USER_ROLES = {
  user: 'user',
  admin: 'admin',
  superadmin: 'superadmin',
} as const;

export const USER_ROLE_LABELS: Record<string, string> = {
  user: 'Usuário',
  admin: 'Administrador',
  superadmin: 'Super Admin',
} as const;

// -----------------------------------------------------------------------------
// Status Labels
// -----------------------------------------------------------------------------
export const STATUS_LABELS = {
  active: 'Ativo',
  inactive: 'Inativo',
  pending: 'Pendente',
  verified: 'Verificado',
  unverified: 'Não Verificado',
} as const;

// -----------------------------------------------------------------------------
// Error Messages
// -----------------------------------------------------------------------------
export const ERROR_MESSAGES = {
  generic: 'Ocorreu um erro. Tente novamente.',
  unauthorized: 'Você não tem permissão para acessar esta página.',
  notFound: 'Recurso não encontrado.',
  networkError: 'Erro de conexão. Verifique sua internet.',
  loadUsers: 'Erro ao carregar usuários.',
  loadAgents: 'Erro ao carregar agentes.',
  loadDatabases: 'Erro ao carregar bancos de dados.',
  loadDatasources: 'Erro ao carregar data sources.',
  updateUser: 'Erro ao atualizar usuário.',
  deleteUser: 'Erro ao excluir usuário.',
  createAgent: 'Erro ao criar agente.',
  updateAgent: 'Erro ao atualizar agente.',
  deleteAgent: 'Erro ao excluir agente.',
} as const;

// -----------------------------------------------------------------------------
// Success Messages
// -----------------------------------------------------------------------------
export const SUCCESS_MESSAGES = {
  userUpdated: 'Usuário atualizado com sucesso.',
  userDeleted: 'Usuário excluído com sucesso.',
  agentCreated: 'Agente criado com sucesso.',
  agentUpdated: 'Agente atualizado com sucesso.',
  agentDeleted: 'Agente excluído com sucesso.',
  databaseCreated: 'Banco de dados criado com sucesso.',
  databaseUpdated: 'Banco de dados atualizado com sucesso.',
  databaseDeleted: 'Banco de dados excluído com sucesso.',
  datasourceCreated: 'Data source criado com sucesso.',
  datasourceUpdated: 'Data source atualizado com sucesso.',
  datasourceDeleted: 'Data source excluído com sucesso.',
} as const;

// -----------------------------------------------------------------------------
// Time Ranges (for dashboard filters)
// -----------------------------------------------------------------------------
export const TIME_RANGES = {
  today: 'today',
  week: 'week',
  month: 'month',
  year: 'year',
} as const;

export const TIME_RANGE_LABELS: Record<string, string> = {
  today: 'Hoje',
  week: 'Última Semana',
  month: 'Último Mês',
  year: 'Último Ano',
} as const;

// -----------------------------------------------------------------------------
// Loading Messages
// -----------------------------------------------------------------------------
export const LOADING_MESSAGES = {
  default: 'Carregando...',
  agents: 'Carregando agentes...',
  databases: 'Carregando bancos de dados...',
  datasources: 'Carregando data sources...',
  saving: 'Salvando...',
  deleting: 'Excluindo...',
} as const;
